import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, RotateCcw, Sun, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { CONTACT } from "@/config/site";
import { WhatsAppIcon } from "./WhatsAppIcon";

const LS_KEY = "anuj_chat_session";

const Bubble = ({ m, i }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div
      data-testid={`chat-message-${m.role}-${i}`}
      className={`max-w-[85%] whitespace-pre-line rounded-2xl px-4 py-3 text-[16px] leading-relaxed ${
        m.role === "user" ? "rounded-br-md bg-gold text-navy-950 font-medium" : "rounded-bl-md border border-slate-200 bg-slate-100 text-slate-800"
      }`}
    >
      {m.text}
    </div>
  </div>
);

const Typing = () => (
  <div data-testid="chat-typing-indicator" className="flex w-16 items-center gap-1.5 rounded-2xl rounded-bl-md border border-slate-200 bg-slate-100 px-4 py-4">
    {[0, 1, 2].map((d) => (
      <motion.span key={d} className="h-2 w-2 rounded-full bg-gold" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }} />
    ))}
  </div>
);

const DoneActions = () => (
  <div className="grid grid-cols-2 gap-2" data-testid="chat-done-actions">
    <a href={CONTACT.tel} data-testid="chat-done-call-btn" className="btn-gold h-12 text-[16px]"><Phone className="h-4 w-4" /> Call करें</a>
    <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="chat-done-whatsapp-btn" className="btn-wa h-12 text-[16px]"><WhatsAppIcon className="h-4 w-4" /> WhatsApp</a>
  </div>
);

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [replies, setReplies] = useState([]);
  const [done, setDone] = useState(false);
  const [step, setStep] = useState("");
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState("");
  const [teaser, setTeaser] = useState(false);
  const sid = useRef(localStorage.getItem(LS_KEY));
  const started = useRef(false);
  const endRef = useRef(null);

  const call = useCallback(async (body) => {
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sid.current, ...body });
      sid.current = data.session_id;
      localStorage.setItem(LS_KEY, data.session_id);
      setMsgs(data.transcript);
      setReplies(data.quick_replies);
      setDone(data.done);
      setStep(data.step);
    } catch {
      setMsgs((m) => [...m, { role: "bot", text: `Network problem. Please try again or call: ${CONTACT.phone}` }]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("open-chat", h);
    const t = setTimeout(() => setTeaser(true), 4000);
    return () => { window.removeEventListener("open-chat", h); clearTimeout(t); };
  }, []);

  useEffect(() => {
    if (open && !started.current) { started.current = true; call({}); }
  }, [open, call]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs, loading, open, replies]);

  const send = (text, qr = false) => {
    const t = text.trim();
    if (!t || loading) return;
    setMsgs((m) => [...m, { role: "user", text: t }]);
    setReplies([]);
    setInput("");
    call({ text: t, quick_reply: qr });
  };

  const restart = () => {
    localStorage.removeItem(LS_KEY);
    sid.current = null;
    setMsgs([]);
    setDone(false);
    call({});
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-widget-panel"
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="glass-strong fixed inset-0 z-50 flex flex-col overflow-hidden sm:inset-auto sm:bottom-6 sm:right-6 sm:h-[680px] sm:max-h-[calc(100vh-3rem)] sm:w-[410px] sm:rounded-3xl sm:shadow-[0_30px_80px_-20px_rgba(15,23,42,0.35)]"
          >
            <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3.5">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-gold"><Sun className="h-6 w-6 text-navy-950" /></span>
              <div className="flex-1 leading-tight">
                <div className="font-display text-lg font-bold text-navy-900">Anuj Solar Assistant</div>
                <div className="flex items-center gap-1.5 text-sm text-emerald-700"><span className="h-2 w-2 rounded-full bg-emerald-400" /> Online · AI Assistant</div>
              </div>
              <button onClick={restart} data-testid="chat-restart-btn" aria-label="Restart" className="grid h-10 w-10 place-items-center rounded-full text-slate-600 hover:bg-slate-100"><RotateCcw className="h-5 w-5" /></button>
              <button onClick={() => setOpen(false)} data-testid="chat-close-btn" aria-label="Close" className="grid h-10 w-10 place-items-center rounded-full text-slate-600 hover:bg-slate-100"><X className="h-6 w-6" /></button>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-5" data-testid="chat-messages">
              {msgs.map((m, i) => <Bubble key={i} m={m} i={i} />)}
              {loading && <Typing />}
              {!loading && replies.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-quick-replies">
                  {replies.map((r, i) => (
                    <button key={r} onClick={() => send(r, true)} data-testid={`chat-quick-reply-${i}`} className="min-h-[44px] rounded-full border border-gold/50 bg-gold/10 px-4 py-2 text-[15px] font-semibold text-gold-ink transition-[background-color,color,transform] duration-200 hover:bg-gold hover:text-navy-950 active:scale-95">
                      {r}
                    </button>
                  ))}
                </div>
              )}
              {done && !loading && <DoneActions />}
              <div ref={endRef} />
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex items-center gap-2 border-t border-slate-200 bg-white p-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                inputMode={step === "mobile" ? "tel" : "text"}
                placeholder={step === "mobile" ? "10-digit mobile number" : "Type here or ask a question…"}
                data-testid="chat-input"
                className="h-12 flex-1 rounded-full border border-slate-200 bg-white px-5 text-[16px] text-navy-900 placeholder:text-slate-500 outline-none focus:border-gold/60"
              />
              <button type="submit" disabled={loading || !input.trim()} data-testid="chat-send-btn" aria-label="Send" className="grid h-12 w-12 place-items-center rounded-full bg-gold text-navy-950 transition-transform hover:scale-105 disabled:opacity-40">
                <Send className="h-5 w-5" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {!open && (
        <div className="fixed bottom-5 right-5 sm:right-7 z-40 flex items-center gap-3">
          <AnimatePresence>
            {teaser && (
              <motion.button
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}
                onClick={() => setOpen(true)} data-testid="chat-teaser-btn"
                className="glass-strong hidden sm:block rounded-2xl rounded-br-sm px-4 py-2.5 text-[15px] font-semibold text-navy-900"
              >
                Book Free Site Visit →
              </motion.button>
            )}
          </AnimatePresence>
          <button onClick={() => setOpen(true)} data-testid="chat-widget-toggle-btn" aria-label="Chat" className="grid h-16 w-16 place-items-center rounded-full bg-gold text-navy-950 shadow-[0_14px_36px_-8px_rgba(245,158,11,0.75)] transition-transform duration-300 hover:scale-110">
            <MessageCircle className="h-7 w-7" />
          </button>
        </div>
      )}
    </>
  );
};
